import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { crm, crmFileUrl, uploadCrmDoc } from './crmApi';
import { P2PCard } from './P2P';
import type { CallFix, CrmActivity, CrmAuth, CrmLead, CrmMe, FixKind, Outcome, PollAnswer } from './crmApi';
import LogCallModal from './LogCallModal';
import { MeetingCard } from './Meetings';
import PollModal from './PollModal';
import { Journey, LeadTypeChip, Modal, SkillChips, Spinner, StageChip, TONE, WAIT_LABEL, fmtPhone, fmtRel, fmtWhen, inputCls, labelCls, nextStep, ownerLine, purpleBg , webFormLabel } from './ui';

// ── The lead page ────────────────────────────────────────────
// How far they got, what to do next (with the poll's status buttons, or
// "They paid" for an Interested lead, or the coordinator meeting's card),
// the details, the history (whoever logged the last call can ask to undo it:
// a call fix request), and for managers: move, edit and delete. Mirrors the
// app's crm_lead_screen.dart.

// A coordinator meeting's line in the history (and what the coordinator did after it)
export const meetingLabel = (a: CrmActivity): string => {
  switch (a.result) {
    case 'Booked':          return `${a.byName} booked a meeting with ${a.toName}`;
    case 'Moved':           return `${a.byName} moved the meeting${a.toName ? ` (now with ${a.toName})` : ''}`;
    case 'Link added':      return `${a.byName} added the meeting link`;
    case 'Link changed':    return `${a.byName} changed the meeting link`;
    case 'Sent to student': return `${a.byName} sent the meeting to the student`;
    case 'Joined':          return `${a.byName}: the student joined the meeting${a.pollSentAt ? ', brochure sent' : ''}`;
    case "Didn't join":     return `${a.byName}: the student didn't join the meeting`;
    case 'Cancelled':       return `${a.byName} cancelled the meeting`;
    case 'Poll Sent':       return `${a.byName} (coordinator): poll sent again`;
    case 'Converted':       return `${a.byName} (coordinator): Converted`;
    case 'Form filled':     return '📝 The student filled the registration form';
    default:                return `${a.byName} (coordinator): poll status → ${a.result}`;
  }
};
const MEETING_TIMES = ['Booked', 'Moved', 'Link added', 'Link changed', 'Sent to student', 'Joined', "Didn't join", 'Cancelled'];
// A call that only marked a Direct Visit "Didn't come" (the lead went to No Response)
const VISIT_MISSED = "Didn't come";
export const nextLabel = (a: CrmActivity) => (a.type === 'meeting' ? (MEETING_TIMES.includes(a.result) ? 'Meeting' : 'Pays')
  : a.result === 'Poll Sent' ? 'Status due' : a.type === 'poll' ? 'Pays'
  : a.outcome === 'Coordinator Meeting' || a.result === 'Coordinator Meeting' ? 'Meeting' : 'Next');
// "Poll sent 5:30 PM", or "Poll goes out 5:30 PM" while it is still to come
export const pollLabel = (at: string) => `Poll ${new Date(at).getTime() > Date.now() ? 'goes out' : 'sent'} ${fmtWhen(at)}`;

const typeLabel = (a: CrmActivity): string => {
  switch (a.type) {
    case 'added':    return `Added by ${a.byName}${a.source ? ` · ${a.source}` : ''}`;
    case 'assigned': return `Given to ${a.toName}`;
    case 'moved':    return `Moved${a.fromName ? ` from ${a.fromName}` : ''} to ${a.toName}`;
    case 'waiting':  return `Waiting list${a.fromName ? ` (was ${a.fromName})` : ''}`;
    case 'junk':     return `${a.byId ? `Put in Junk Leads by ${a.byName}` : 'Junk Leads'}${a.fromName ? ` (was ${a.fromName})` : ''}`;
    case 'enquired': return `Enquired again${a.source ? ` through ${a.source}` : ''}`;
    case 'edited':   return `Edited by ${a.byName}`;
    case 'poll':     return `${a.byName}: poll status → ${a.result}`;
    case 'meeting':  return meetingLabel(a);
    case 'p2p':      return `P2P · ${a.result}${a.byName && a.byName !== 'System' && !a.result.includes(a.byName) ? ` · ${a.byName}` : ''}`;
    case 'deleted':  return `Deleted by ${a.byName}`;
    case 'restored': return `Restored by ${a.byName}`;
    default:         return a.outcome === VISIT_MISSED ? `${a.byName}: didn't come for the visit → No Response` : `${a.byName}: ${callText(a)}`;
  }
};
// A call: its outcome, what was heard (Not Picked) and its result
export const callText = (a: CrmActivity) => `${a.outcome}${a.heard ? ` · ${a.heard}` : ''}${a.result ? ` → ${a.result}` : ''}`;

export const ICON: Record<string, string> = {
  added: '➕', assigned: '👤', moved: '🔀', waiting: '⏳', junk: '🚮', enquired: '🔁', edited: '✏️', call: '📞', poll: '📊', meeting: '🤝', p2p: '🎯',
  'cancelled-call': '🚫', deleted: '🗑️', restored: '♻️',
};

// The label is the lead's stage; after the brochure it stays (it holds the
// student's answer) even when the last call wasn't picked: say so beside it
export const lastCallNote = (l: CrmLead) => (l.lastOutcome === 'Not Picked' && l.lastCallAt && !['New', 'Not Picked'].includes(l.stage)
  ? `Last call: Not picked · ${fmtWhen(l.lastCallAt)}` : '');

const FIX_STATUS: Record<CallFix['status'], { label: string; cls: string }> = {
  Pending:  { label: 'Fix asked: waiting for approval', cls: 'bg-amber-50 text-amber-800' },
  Approved: { label: 'Fix approved', cls: 'bg-green-50 text-green-800' },
  Rejected: { label: 'Fix not approved', cls: 'bg-red-50 text-red-700' },
};
export const fixWhat = (r: CallFix) => (r.kind === 'wrong-lead' ? 'logged on the wrong lead' : `should be ${r.should}`);

// Whoever logged the last call: it was wrong, undo it (a manager approves)
const FALLBACK_SHOULD = ['Not Picked', 'Call Later', 'Cold Lead', 'Brochure Sent → Poll Sent', 'Brochure Sent → Future Prospect', 'Brochure Sent → Direct Visit', 'Coordinator Meeting', 'Converted'];
export const AskFixModal: React.FC<{ auth: CrmAuth; me: CrmMe; leadId: string; call: CrmActivity; onClose: () => void; onSent: () => void }> = ({ auth, me, leadId, call, onClose, onSent }) => {
  const now = `${call.outcome}${call.result ? ` → ${call.result}` : ''}`;
  const options = (me.fixShould || FALLBACK_SHOULD).filter(o => o !== now);
  const [kind, setKind] = useState<FixKind>('wrong-result');
  const [should, setShould] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const send = async () => {
    if (kind === 'wrong-result' && !should) { toast.error('Pick what it should have been'); return; }
    if (!reason.trim()) { toast.error('Say why: what really happened on the call'); return; }
    setSaving(true);
    try {
      await crm.askCallFix(auth, { leadId, activityId: call.id, kind, should: kind === 'wrong-result' ? should : '', reason: reason.trim() });
      toast.success('Sent. A Lead Manager will approve or turn it down.');
      onSent();
    } catch (e: any) { toast.error(e.message); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Ask to fix this call" sub={`${now} · ${fmtWhen(call.at)}`} onClose={onClose}>
      <div className="space-y-3">
        <div role="radiogroup" aria-label="What is wrong" className="space-y-2">
          {([['wrong-result', 'The result is wrong'], ['wrong-lead', 'Logged on the wrong lead']] as [FixKind, string][]).map(([k, label]) => (
            <label key={k} className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 cursor-pointer text-sm font-semibold ${kind === k ? 'border-purple-400 bg-purple-50 text-purple-900' : 'border-gray-200 text-gray-700'}`}>
              <input type="radio" name="fix-kind" checked={kind === k} onChange={() => setKind(k)} className="accent-purple-700" />{label}
            </label>
          ))}
        </div>
        {kind === 'wrong-result' && (
          <div>
            <label className={labelCls} htmlFor="fix-should">It should be</label>
            <select id="fix-should" value={should} onChange={e => setShould(e.target.value)} className={inputCls}>
              <option value="">Pick…</option>{options.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        )}
        <div><label className={labelCls} htmlFor="fix-reason">Why</label><input id="fix-reason" value={reason} onChange={e => setReason(e.target.value)} className={inputCls} placeholder="What really happened on the call" /></div>
        <p className="text-[11px] text-gray-500">A Lead Manager or the admin approves it. Then the call is crossed out, the lead goes back to how it was before it, and you log the right result.</p>
        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600">Back</button>
          <button type="button" onClick={send} disabled={saving} className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-60" style={purpleBg}>{saving ? 'Sending…' : 'Send for approval'}</button>
        </div>
      </div>
    </Modal>
  );
};

// A Lead Manager, Sales Admin or the admin: delete the lead (30 days in Deleted leads)
const DELETE_REASONS = ['Wrong number', 'Duplicate lead', 'Test lead', 'Asked not to be called'];
const DeleteLeadModal: React.FC<{ auth: CrmAuth; lead: CrmLead; onClose: () => void; onDeleted: () => void }> = ({ auth, lead, onClose, onDeleted }) => {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const del = async () => {
    if (!reason.trim()) { toast.error('Say why the lead is deleted'); return; }
    setSaving(true);
    try {
      await crm.deleteLead(auth, lead.id, reason.trim());
      toast.success(`Lead #${lead.leadNo} deleted. It stays in Deleted leads for 30 days.`);
      onDeleted();
    } catch (e: any) { toast.error(e.message); }
    finally { setSaving(false); }
  };
  return (
    <Modal title={`Delete lead #${lead.leadNo}?`} sub={`${lead.name || fmtPhone(lead.phone)} · ${lead.stage}`} onClose={onClose}>
      <div className="space-y-3">
        <div className="flex flex-wrap gap-1.5">
          {DELETE_REASONS.map(r => <button key={r} type="button" onClick={() => setReason(r)} className={`px-2.5 py-1 rounded-lg text-[11px] font-bold border ${reason === r ? 'border-red-300 bg-red-50 text-red-700' : 'border-gray-200 text-gray-600'}`}>{r}</button>)}
        </div>
        <div><label className={labelCls} htmlFor="del-reason">Why?</label><input id="del-reason" value={reason} onChange={e => setReason(e.target.value)} className={inputCls} /></div>
        <p className="text-[11px] text-gray-500">It goes to Leads → Deleted for 30 days, where it can be restored, then it is gone for good. The calls made on it still count on the Monitor. If the number comes in again, it is a new lead.</p>
        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600">Back</button>
          <button type="button" onClick={del} disabled={saving} className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-red-600 hover:bg-red-700 disabled:opacity-60">{saving ? 'Deleting…' : 'Delete the lead'}</button>
        </div>
      </div>
    </Modal>
  );
};
// A Lead Manager, Sales Admin or the admin: off everyone's lists to Junk Leads
// (not converted, in P2P, or with a coordinator meeting or coordinator)
const JUNK_FALLBACK = ['Wrong number', 'Out of service', 'Not reachable', 'Fake / test lead', 'Duplicate', 'Other'];
export const junkBlock = (l: CrmLead): string => (l.stage === 'Junk Lead' ? 'It is in Junk Leads already'
  : l.stage === 'Converted' ? 'It has converted'
  : l.stage === 'Meeting Booked' ? 'It has a coordinator meeting booked'
  : l.stage === 'P2P' ? 'It is in Path to Placement (P2P)'
  : l.coordinatorName ? `${l.coordinatorName} (Student Coordinator) handles it` : '');
export const JunkLeadModal: React.FC<{ auth: CrmAuth; me: CrmMe; leadIds: string[]; title: string; sub: string; onClose: () => void; onDone: (moved: number, skipped: number) => void }> = ({ auth, me, leadIds, title, sub, onClose, onDone }) => {
  const reasons = me.junkReasons || JUNK_FALLBACK;
  const [pick, setPick] = useState('');
  const [other, setOther] = useState('');
  const [saving, setSaving] = useState(false);
  const reason = pick === 'Other' ? other.trim() : pick;
  const go = async () => {
    if (!reason) { toast.error(pick === 'Other' ? 'Type the reason' : 'Pick why'); return; }
    setSaving(true);
    try {
      const r = await crm.moveLeads(auth, leadIds, 'junk', reason);
      onDone(r.moved, r.skipped);
    } catch (e: any) { toast.error(e.message); }
    finally { setSaving(false); }
  };
  return (
    <Modal title={title} sub={sub} onClose={onClose}>
      <div className="space-y-3">
        <p className={labelCls}>Why?</p>
        <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Why">
          {reasons.map(r => (
            <button key={r} type="button" role="radio" aria-checked={pick === r} onClick={() => setPick(r)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold border-2 ${pick === r ? 'border-pink-400 bg-pink-50 text-pink-800' : 'border-gray-200 text-gray-600'}`}>{r}</button>
          ))}
        </div>
        {pick === 'Other' && <input value={other} onChange={e => setOther(e.target.value)} className={inputCls} placeholder="Type the reason" aria-label="The reason" />}
        <p className="text-[11px] text-gray-500">It leaves everyone's lists at once. From Junk Leads you can Move it to someone to try again; if the student enquires again, it starts over by itself.</p>
        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600">Back</button>
          <button type="button" onClick={go} disabled={saving} className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-pink-700 hover:bg-pink-800 disabled:opacity-60">{saving ? 'Moving…' : '🚮 Move to Junk Leads'}</button>
        </div>
      </div>
    </Modal>
  );
};

const POLL_BUTTONS: { key: PollAnswer; label: string; cls: string }[] = [
  { key: 'Interested', label: '✅ Interested', cls: 'bg-green-600 hover:bg-green-700 text-white' },
  { key: 'Not Interested', label: '❌ Not interested', cls: 'bg-white border-2 border-red-200 text-red-700 hover:bg-red-50' },
  { key: 'No Response', label: '🔕 No response', cls: 'bg-white border-2 border-gray-200 text-gray-700 hover:bg-gray-50' },
];

const Recording: React.FC<{ auth: CrmAuth; activity: CrmActivity; file: 'recording' | 'screenshot' }> = ({ auth, activity, file }) => {
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const info = activity[file]!;
  const open = async () => {
    setBusy(true);
    try {
      const r = await crm.fileLink(auth, activity.id, file);
      const full = crmFileUrl(r.url);
      if (file === 'screenshot') window.open(full, '_blank', 'noopener');
      else setUrl(full);
    } catch (e: any) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  if (url) {
    return (
      <div className="mt-2 space-y-1">
        <audio controls autoPlay src={url} className="w-full max-w-sm" />
        <a href={url} target="_blank" rel="noreferrer" className="text-[11px] text-purple-600 font-semibold">Download {info.fileName}</a>
      </div>
    );
  }
  return (
    <button onClick={open} disabled={busy} className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg border border-purple-200 text-purple-700 hover:bg-purple-50 disabled:opacity-60">
      {file === 'recording' ? '▶ Play recording' : '🖼 View screenshot'}
      <span className="font-medium text-gray-400">{info.fileName} · {(info.size / 1048576).toFixed(1)} MB</span>
    </button>
  );
};

// The resume that came with the /p2p page form, opened in a new tab
export const ResumeButton: React.FC<{ auth: CrmAuth; leadId: string; resume: { fileName: string; size: number }; compact?: boolean }> = ({ auth, leadId, resume, compact }) => {
  const [busy, setBusy] = useState(false);
  const open = async () => {
    setBusy(true);
    try {
      const r = await crm.resumeLink(auth, leadId);
      window.open(crmFileUrl(r.url), '_blank', 'noopener');
    } catch (e: any) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  return (
    <button onClick={open} disabled={busy} title={resume.fileName}
      className={compact ? 'px-3 py-1.5 rounded-lg text-[11px] font-bold border border-purple-200 text-purple-700 hover:bg-purple-50 disabled:opacity-60' : 'px-3 py-2.5 rounded-xl text-xs font-bold border-2 border-purple-200 text-purple-700 hover:bg-purple-50 disabled:opacity-60'}>
      📄 Resume <span className="font-medium text-gray-400">{resume.size < 1048576 ? `${Math.max(1, Math.round(resume.size / 1024))} KB` : `${(resume.size / 1048576).toFixed(1)} MB`}</span>
    </button>
  );
};

// A lead with no resume yet (a P2P meeting needs it within 30 minutes)
const AddResume: React.FC<{ auth: CrmAuth; leadId: string; onDone: () => void }> = ({ auth, leadId, onDone }) => {
  const [busy, setBusy] = useState(false);
  const pick = async (f: File | null) => {
    if (!f) return;
    if (!/\.(pdf|docx?)$/i.test(f.name)) { toast.error('Pick a PDF or Word file (.pdf, .doc, .docx)'); return; }
    if (f.size > 5 * 1048576) { toast.error('The resume is too big (most 5 MB)'); return; }
    setBusy(true);
    try { await uploadCrmDoc(auth, leadId, 'resume', f); toast.success('Resume added'); onDone(); }
    catch (e: any) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  return (
    <label className={`px-3 py-2.5 rounded-xl text-xs font-bold border-2 border-dashed border-amber-300 text-amber-800 hover:bg-amber-50 cursor-pointer ${busy ? 'opacity-60' : ''}`}>
      {busy ? 'Uploading…' : '📄 Add resume'}
      <input type="file" accept=".pdf,.doc,.docx" className="hidden" disabled={busy} onChange={e => { pick(e.target.files?.[0] || null); e.target.value = ''; }} />
    </label>
  );
};

// The poll's status buttons, also used in the Today list
export const PollButtons: React.FC<{ onPick: (a: PollAnswer) => void; small?: boolean }> = ({ onPick, small }) => (
  <div className="flex flex-wrap gap-2">
    {POLL_BUTTONS.map(b => (
      <button key={b.key} type="button" onClick={e => { e.stopPropagation(); onPick(b.key); }}
        className={`${small ? 'px-3 py-1.5 text-[11px]' : 'px-4 py-2 text-xs'} rounded-xl font-bold transition-colors ${b.cls}`}>{b.label}</button>
    ))}
  </div>
);

// logOutcome: open Log call with it (an approved call fix: log the right result)
const LeadPanel: React.FC<{ auth: CrmAuth; leadId: string; me: CrmMe; onClose: () => void; onChanged: () => void; logOutcome?: Outcome }> = ({ auth, leadId, me, onClose, onChanged, logOutcome }) => {
  const [data, setData] = useState<Awaited<ReturnType<typeof crm.getLead>> | null>(null);
  const [logging, setLogging] = useState<Outcome | '' | null>(logOutcome ?? null);
  const [polling, setPolling] = useState<PollAnswer | '' | null>(null);
  const [editing, setEditing] = useState(false);
  const [moveTo, setMoveTo] = useState('');
  const [form, setForm] = useState({ name: '', email: '', course: '', source: '', notes: '', leadType: '', mode: '' });
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [junking, setJunking] = useState(false);
  const [fixing, setFixing] = useState<CrmActivity | null>(null);

  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const load = useCallback(async () => {
    try {
      const r = await crm.getLead(auth, leadId);
      setData(r);
      setForm({ name: r.lead.name, email: r.lead.email, course: r.lead.course, source: r.lead.source, notes: r.lead.notes, leadType: r.lead.leadType || '', mode: r.lead.mode || '' });
    } catch (e: any) { toast.error(e.message); closeRef.current(); }
  }, [auth, leadId]);
  useEffect(() => { load(); }, [load]);

  const move = async () => {
    if (!moveTo || !data) return;
    setBusy(true);
    try {
      const r = await crm.moveLeads(auth, [data.lead.id], moveTo);
      toast.success(r.moved ? 'Moved' : r.waiting ? 'Nobody free: it waits for someone' : 'Nothing to move');
      setMoveTo('');
      await load();
      onChanged();
    } catch (e: any) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const saveEdit = async () => {
    if (!data) return;
    setBusy(true);
    try {
      await crm.updateLead(auth, data.lead.id, form);
      toast.success('Saved');
      setEditing(false);
      await load();
      onChanged();
    } catch (e: any) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const saved = () => { setLogging(null); setPolling(null); load(); onChanged(); };

  if (!data) return <Modal title="Lead" onClose={onClose}><Spinner label="Loading…" /></Modal>;
  const { lead: l, history, canLog, canPoll, maxOwners, meeting, lockedBy, callFix, p2pCase } = data;
  const people = (me.people || []).filter(p => p.id !== l.ownerId);
  const next = nextStep(l);
  const pollStage = l.stage === 'Poll Sent' || l.stage === 'No Response';
  const lastCall = lastCallNote(l);
  const fixOf = (a: CrmActivity) => callFix?.requests.find(r => r.activityId === a.id);

  // What to do next, with the button for it
  const nextCard = (() => {
    // In Path to Placement: its card (a Lead Manager's buttons, or the salesperson's)
    if (p2pCase && p2pCase.step !== 'done') return <P2PCard auth={auth} c={p2pCase} onChange={() => { load(); onChanged(); }} />;
    if (l.stage === 'Converted') return null;
    // The coordinator meeting: send its link, change or cancel it
    if (meeting && meeting.active) return <MeetingCard auth={auth} m={meeting} onChanged={() => { load(); onChanged(); }} />;
    // Since the meeting a coordinator handles it (managers can still step in)
    if (l.coordinatorName && !me.me.isManager && !['Cold Lead', 'Not Interested'].includes(l.stage)) {
      return (
        <div className="rounded-2xl p-4 bg-indigo-50 border-2 border-indigo-100">
          <p className="font-bold text-indigo-900">🤝 {l.coordinatorName} (Student Coordinator) handles this lead</p>
          <p className="text-xs text-gray-600 mt-0.5">The student joined the meeting. The coordinator sends the brochure and the poll, follows up and marks them Converted.</p>
        </div>
      );
    }
    if (canPoll && pollStage) {
      return (
        <div className={`rounded-2xl p-4 space-y-3 ${l.pollDue ? 'bg-red-50 border-2 border-red-200' : 'bg-fuchsia-50 border-2 border-fuchsia-100'}`}>
          <div>
            <p className={`font-bold ${l.pollDue ? 'text-red-800' : 'text-fuchsia-900'}`}>
              {l.stage === 'No Response' ? l.reason || 'No reply to the poll yet' : l.pollDue ? 'Update the poll status now' : `Update the poll status by ${fmtWhen(l.nextAt)}`}
            </p>
            <p className="text-xs text-gray-600 mt-0.5">{l.pollSentAt ? `${pollLabel(l.pollSentAt)}. ` : ''}What did they say?</p>
          </div>
          <PollButtons onPick={setPolling} />
        </div>
      );
    }
    if (l.stage === 'Interested') {
      return (
        <div className={`rounded-2xl p-4 space-y-3 ${l.paymentDue ? 'bg-red-50 border-2 border-red-200' : 'bg-green-50 border-2 border-green-100'}`}>
          <div>
            <p className={`font-bold ${l.paymentDue ? 'text-red-800' : 'text-green-900'}`}>{l.paymentDue ? 'They said they would pay by now' : l.nextAt ? `Interested: they will pay ${fmtWhen(l.nextAt)}` : 'Interested: waiting for their payment'}</p>
            <p className="text-xs text-gray-600 mt-0.5">When they pay, mark them Converted. That makes their payment record.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {canLog && <button onClick={() => setLogging('Converted')} className="px-4 py-2 rounded-xl text-xs font-bold text-white bg-green-600 hover:bg-green-700">💰 They paid: Converted</button>}
            {canPoll && <button onClick={() => setPolling('')} className="px-4 py-2 rounded-xl text-xs font-bold border-2 border-gray-200 bg-white text-gray-700 hover:bg-gray-50">Change the poll status</button>}
          </div>
        </div>
      );
    }
    if (canPoll && l.stage === 'Not Interested') {
      return (
        <div className="rounded-2xl p-4 bg-gray-50 border-2 border-gray-100 flex flex-wrap items-center gap-3 justify-between">
          <p className="text-sm text-gray-700"><b>Said not interested</b>{l.reason ? `: ${l.reason}` : ''}. Changed their mind?</p>
          <button onClick={() => setPolling('')} className="px-4 py-2 rounded-xl text-xs font-bold border-2 border-gray-200 bg-white text-gray-700 hover:bg-gray-100">Update the poll status</button>
        </div>
      );
    }
    return (
      <div className={`rounded-xl px-4 py-3 text-sm ${next.tone === 'red' ? 'bg-red-50' : 'bg-purple-50'}`}>
        <b className={TONE[next.tone]}>Next: {next.text}</b>{l.nextAt && !['Cold Lead', 'Not Interested'].includes(l.stage) ? <span className="text-gray-500"> · {fmtRel(l.nextAt)}</span> : null}
      </div>
    );
  })();

  return (
    <>
      <Modal title={l.name || fmtPhone(l.phone)} sub={`#${l.leadNo} · ${ownerLine(l, maxOwners)}`} onClose={onClose} wide>
        <div className="space-y-5">
          <Journey lead={l} />
          <div className="flex items-center gap-2 flex-wrap">
            <StageChip stage={l.stage} />
            {lastCall && <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-gray-100 text-gray-600">{lastCall}</span>}
            <LeadTypeChip type={l.leadType} />
            {l.p2p && <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-amber-100 text-amber-800">🎯 P2P · Path to Placement{l.p2p.outcome ? ` · ${l.p2p.outcome}` : ''}</span>}
            {l.waiting &&<span className="text-xs font-bold px-2.5 py-1 rounded-full bg-amber-100 text-amber-800">{WAIT_LABEL[l.waiting]}</span>}
            {l.turnNote
              ? <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-amber-100 text-amber-800">{l.turnNote}</span>
              : l.movesTomorrow && <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-orange-100 text-orange-700">Moves to the next person tomorrow {l.stage === 'Call Later' ? 'unless a brochure goes' : 'if not taken'}</span>}
            {l.webForm && <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-amber-100 text-amber-800">{webFormLabel(l.webForm)}</span>}
            {l.enquiries > 1 && <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-sky-100 text-sky-700">Enquired {l.enquiries} times</span>}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <a href={`tel:+91${l.phone}`} className="px-4 py-2.5 rounded-xl text-sm font-bold text-white" style={{ background: 'linear-gradient(135deg,#16a34a,#15803d)' }}>📞 Call {fmtPhone(l.phone)}</a>
            <button onClick={() => navigator.clipboard?.writeText(l.phone).then(() => toast.success('Number copied'), () => {})}
              className="px-3 py-2.5 rounded-xl text-xs font-bold border-2 border-gray-200 text-gray-600 hover:bg-gray-50">Copy number</button>
            {l.resume ? <ResumeButton auth={auth} leadId={l.id} resume={l.resume} />
              : (me.me.isManager || l.ownerId === me.me.id) && <AddResume auth={auth} leadId={l.id} onDone={() => { load(); onChanged(); }} />}
            {canLog &&<button onClick={() => setLogging('')} className="px-4 py-2.5 rounded-xl text-sm font-bold text-white" style={purpleBg}>Log call</button>}
          </div>
          {lockedBy && !(meeting && meeting.active) && !l.coordinatorName && !(p2pCase && p2pCase.step !== 'done') && <p className="text-xs text-gray-500">{lockedBy}</p>}

          {nextCard}

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            {[
              ['Course', l.course], ['Online / offline', l.mode], ['Source', l.source], ['Email', l.email],
              ['Degree', l.degree], ['Passed out', l.passedOutYear], ['Profile', l.profile],
              ['Location', l.location], ['Calls', String(l.callCount)], ['Reason', l.reason],
            ].filter(([, v]) => v).map(([k, v]) => (
              <div key={k} className="min-w-0">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">{k}</p>
                <p className="text-gray-800 font-medium break-words">{v}</p>
              </div>
            ))}
          </div>
          {l.skills && l.skills.length > 0 && (
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1.5">Skills</p>
              <SkillChips skills={l.skills} />
            </div>
          )}
          {(l.notes || l.pitchNotes) && (
            <div className="text-sm space-y-1">
              {l.notes && <p className="text-gray-600"><b className="text-gray-400 text-xs uppercase">Added with:</b> {l.notes}</p>}
              {l.pitchNotes && <p className="text-gray-600"><b className="text-gray-400 text-xs uppercase">{l.pitched ? 'Brochure notes:' : 'About the student:'}</b> {l.pitchNotes}</p>}
            </div>
          )}

          {me.me.isManager && (
            <div className="rounded-xl border border-gray-200 p-3 space-y-3">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Lead Manager</p>
              <div className="flex gap-2 flex-wrap">
                <select value={moveTo} onChange={e => setMoveTo(e.target.value)} className={`${inputCls} flex-1 min-w-[180px]`} aria-label="Move to">
                  <option value="">Move to…</option>
                  <option value="turn">Back into the turn</option>
                  {people.map(p => <option key={p.id} value={p.id}>{p.name} ({p.role})</option>)}
                </select>
                <button onClick={move} disabled={!moveTo || busy} className="px-4 py-2 rounded-xl text-xs font-bold text-white disabled:opacity-50" style={purpleBg}>Move</button>
                <button onClick={() => setEditing(e => !e)} className="px-4 py-2 rounded-xl text-xs font-bold border-2 border-gray-200 text-gray-600">{editing ? 'Close' : 'Edit details'}</button>
                {l.stage !== 'Junk Lead' && (
                  <button onClick={() => setJunking(true)} disabled={!!junkBlock(l)} title={junkBlock(l) || undefined}
                    className="px-4 py-2 rounded-xl text-xs font-bold border-2 border-pink-100 text-pink-800 hover:bg-pink-50 disabled:opacity-40">🚮 Move to Junk Leads</button>
                )}
                <button onClick={() => setDeleting(true)} disabled={l.stage === 'Converted'} title={l.stage === 'Converted' ? "A converted lead can't be deleted" : undefined}
                  className="px-4 py-2 rounded-xl text-xs font-bold border-2 border-red-100 text-red-700 hover:bg-red-50 disabled:opacity-40">🗑 Delete lead</button>
              </div>
              {editing && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {(['name', 'email', 'course', 'source'] as const).map(k => (
                    <div key={k}><label className={labelCls} htmlFor={`e-${k}`}>{k}</label><input id={`e-${k}`} value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} className={inputCls} /></div>
                  ))}
                  <div>
                    <label className={labelCls} htmlFor="e-type">Lead type</label>
                    <select id="e-type" value={form.leadType} onChange={e => setForm(f => ({ ...f, leadType: e.target.value }))} className={inputCls}>
                      <option value="">Not set</option><option value="WhatsApp">WhatsApp lead</option><option value="Form">Form lead</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="e-mode">Online / offline</label>
                    <select id="e-mode" value={form.mode} onChange={e => setForm(f => ({ ...f, mode: e.target.value }))} className={inputCls}>
                      <option value="">Not set</option><option value="Online">Online</option><option value="Offline">Offline</option>
                    </select>
                  </div>
                  <div className="sm:col-span-2"><label className={labelCls} htmlFor="e-notes">Notes</label><textarea id="e-notes" rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className={`${inputCls} resize-none`} /></div>
                  <button onClick={saveEdit} disabled={busy} className="sm:col-span-2 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-60" style={purpleBg}>Save details</button>
                </div>
              )}
            </div>
          )}

          <div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">History</p>
            <ol className="space-y-2">
              {history.map(a => {
                const fix = fixOf(a);
                const gone = a.type === 'cancelled-call';
                return (
                <li key={a.id} className="flex gap-3 text-sm">
                  <span className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 text-xs" aria-hidden>{ICON[a.type] || '•'}</span>
                  <div className={`flex-1 min-w-0 pb-2 border-b border-gray-100 ${gone ? 'opacity-70' : ''}`}>
                    <div className="flex justify-between gap-2 flex-wrap">
                      <p className={`font-semibold ${gone ? 'text-gray-500 line-through' : 'text-gray-800'}`}>{typeLabel(a)}</p>
                      <p className="text-[11px] text-gray-400 whitespace-nowrap">{fmtWhen(a.at)}</p>
                    </div>
                    {gone && a.cancelled && (
                      <p className="text-xs font-semibold text-red-700">
                        Cancelled: fix approved by {a.cancelled.byName}{a.cancelled.should ? ` (${a.cancelled.should === 'Wrong lead' ? 'logged on the wrong lead' : `should be ${a.cancelled.should}`})` : ''}{a.cancelled.reason ? ` · ${a.cancelled.reason}` : ''}
                      </p>
                    )}
                    {fix && !gone && (
                      <p className={`text-[11px] font-semibold mt-0.5 px-2 py-1 rounded-lg w-fit ${FIX_STATUS[fix.status].cls}`}>
                        {FIX_STATUS[fix.status].label}: {fixWhat(fix)}{fix.status === 'Rejected' ? ` · by ${fix.decidedByName}${fix.note ? `: ${fix.note}` : ''}` : ` · asked by ${fix.byName}`}
                      </p>
                    )}
                    {a.visit && a.outcome !== VISIT_MISSED && <p className="text-xs text-gray-500">Visit: {a.visit === 'came' ? 'they came' : "they didn't come"}</p>}
                    {a.reason && <p className="text-xs text-gray-500">{a.reason}</p>}
                    {a.pollSentAt && <p className="text-xs text-gray-500">{pollLabel(a.pollSentAt)}</p>}
                    {a.nextAt && <p className="text-xs text-gray-500">{nextLabel(a)}: {fmtWhen(a.nextAt)}</p>}
                    {a.amount > 0 && <p className="text-xs text-green-700 font-semibold">Paid ₹{a.amount.toLocaleString('en-IN')}</p>}
                    {a.note && <p className="text-xs text-gray-600 mt-0.5 whitespace-pre-wrap">{a.note}</p>}
                    {a.recording && <Recording auth={auth} activity={a} file="recording" />}
                    {a.screenshot && <Recording auth={auth} activity={a} file="screenshot" />}
                    {callFix?.canAsk && callFix.activityId === a.id && (
                      <button onClick={() => setFixing(a)} className="mt-1.5 block text-[11px] font-bold px-2.5 py-1 rounded-lg border border-amber-200 text-amber-800 hover:bg-amber-50">⚠ Wrong? Ask to fix</button>
                    )}
                  </div>
                </li>
                );
              })}
            </ol>
          </div>
        </div>
      </Modal>
      {logging !== null && (
        <LogCallModal auth={auth} lead={l} me={me} initialOutcome={logging || undefined} onClose={() => setLogging(null)} onSaved={saved} />
      )}
      {polling !== null && (
        <PollModal auth={auth} lead={l} me={me} initial={polling || undefined} onClose={() => setPolling(null)} onSaved={saved} />
      )}
      {fixing && <AskFixModal auth={auth} me={me} leadId={l.id} call={fixing} onClose={() => setFixing(null)} onSent={() => { setFixing(null); load(); }} />}
      {deleting && <DeleteLeadModal auth={auth} lead={l} onClose={() => setDeleting(false)} onDeleted={() => { setDeleting(false); onChanged(); onClose(); }} />}
      {junking && (
        <JunkLeadModal auth={auth} me={me} leadIds={[l.id]} title={`Move lead #${l.leadNo} to Junk Leads?`} sub={`${l.name || fmtPhone(l.phone)} · ${l.stage}${l.ownerName ? ` · ${l.ownerName}` : ''}`}
          onClose={() => setJunking(false)} onDone={() => { setJunking(false); toast.success(`Lead #${l.leadNo} moved to Junk Leads`); load(); onChanged(); }} />
      )}
    </>
  );
};

export default LeadPanel;
